import React from "react";

/**
 * HeroBeat — Bilingual landing-page hero visual.
 *
 * Layers (back→front):
 *   1. <video> (if /hero-video.mp4 exists) OR animated CSS scene fallback
 *   2. Subtle gradient overlay for legibility
 *   3. Pulsing radar ring decoration
 *   4. Animated tagline beat: silhouette icon (hesitation) → B logo + "Pause before you decide"
 *
 * The video loops silently. If video fails to load, the CSS scene takes over.
 */
const T = {
  en: {
    pause: "Pause before you decide",
    scanning: "Scanning · Active",
    moment: "The moment before",
  },
  es: {
    pause: "Pausa antes de decidir",
    scanning: "Escaneando · Activo",
    moment: "El instante antes",
  },
};

export const HeroBeat = ({ language = "en" }) => {
  const lang = language === "es" ? "es" : "en";
  const L = T[lang];

  return (
    <div className="relative aspect-square lg:aspect-square" data-testid="hero-beat">
      <div className="absolute inset-0 rounded-3xl border border-white/10 overflow-hidden glass bg-[#0A0A0A]">
        {/* Layer 0: CSS animated scene — fallback (visible when video doesn't load) */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1530] via-[#1a0f3a] to-[#0a0a0a]" />
          <svg
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[55%] h-auto opacity-80 hero-pulse"
            viewBox="0 0 200 200"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="100" cy="70" r="22" fill="#1a2440" stroke="#3a5588" strokeWidth="1.5" />
            <path
              d="M55 175 Q55 120 100 110 Q145 120 145 175 Z"
              fill="#1a2440"
              stroke="#3a5588"
              strokeWidth="1.5"
            />
            <rect x="125" y="135" width="40" height="6" rx="3" fill="#3a5588" opacity="0.6" className="hero-hand" />
            <circle cx="100" cy="34" r="3" fill="#FFB800" className="hero-think" />
            <circle cx="92" cy="42" r="2" fill="#FFB800" opacity="0.7" className="hero-think hero-think-2" />
            <circle cx="84" cy="48" r="1.5" fill="#FFB800" opacity="0.5" className="hero-think hero-think-3" />
          </svg>
          <div className="absolute inset-0 hero-scanlines opacity-30" />
        </div>

        {/* Layer 1: video — always rendered. Covers CSS scene when it plays.
            If it fails to load, the CSS scene below is visible naturally.
            WebM (VP9) preferred — open codec, plays in all modern browsers including
            stock Chromium. MP4 (H.264) as fallback for older / Apple devices. */}
        <video
          className="absolute inset-0 w-full h-full object-cover bg-transparent"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          data-testid="hero-video"
        >
          <source src="/hero-video.webm" type="video/webm" />
          <source src="/hero-video.mp4" type="video/mp4" />
        </video>

        {/* Layer 2: legibility gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40 pointer-events-none" />

        {/* Layer 3: radar rings (decoration) */}
        <div className="absolute inset-12 rounded-full border border-[#006CFF]/25 pointer-events-none">
          <div className="absolute inset-0 rounded-full radar-sweep opacity-50"></div>
        </div>
        <div className="absolute inset-24 rounded-full border border-white/10 pointer-events-none"></div>

        {/* Layer 4: top status pill */}
        <div className="absolute top-5 left-5 flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/45 backdrop-blur-sm border border-white/10">
          <span className="w-1.5 h-1.5 rounded-full bg-[#00FF88] animate-pulse"></span>
          <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/70">
            {L.scanning}
          </span>
        </div>

        {/* Layer 4: bottom — looping beat: hesitation → B logo + tagline */}
        <div className="absolute inset-x-0 bottom-0 p-6 flex flex-col items-center pointer-events-none">
          <div className="relative w-full max-w-[260px] h-[110px]">
            {/* "The moment before" subtitle — visible first */}
            <div
              className="absolute inset-x-0 top-0 text-center font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 hero-beat-moment"
            >
              · {L.moment} ·
            </div>
            {/* B logo + tagline — fades in cyclically */}
            <div className="absolute inset-x-0 bottom-0 flex flex-col items-center gap-2 hero-beat-tagline">
              <img
                src="/b-icon.png"
                alt=""
                className="w-12 h-12 rounded-lg shadow-[0_0_24px_rgba(0,108,255,0.4)]"
                data-testid="hero-beat-logo"
              />
              <div className="font-display text-base font-bold text-white text-center leading-tight px-2">
                {L.pause}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Animations — scoped via styled tag */}
      <style>{`
        @keyframes hero-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.015); }
        }
        @keyframes hero-think {
          0%, 100% { opacity: 0.2; transform: translateY(0); }
          50% { opacity: 1; transform: translateY(-2px); }
        }
        @keyframes hero-hand {
          0%, 100% { transform: translateY(0); opacity: 0.55; }
          50% { transform: translateY(-3px); opacity: 0.85; }
        }
        @keyframes hero-beat-moment {
          0%   { opacity: 0; transform: translateY(6px); }
          15%  { opacity: 1; transform: translateY(0); }
          45%  { opacity: 1; transform: translateY(0); }
          60%  { opacity: 0; transform: translateY(-6px); }
          100% { opacity: 0; }
        }
        @keyframes hero-beat-tagline {
          0%   { opacity: 0; transform: translateY(10px) scale(0.96); }
          50%  { opacity: 0; transform: translateY(10px) scale(0.96); }
          65%  { opacity: 1; transform: translateY(0) scale(1); }
          92%  { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-4px) scale(0.98); }
        }
        @keyframes hero-scan {
          0% { background-position: 0 0; }
          100% { background-position: 0 40px; }
        }
        .hero-pulse  { animation: hero-pulse 3.6s ease-in-out infinite; }
        .hero-think  { animation: hero-think 1.8s ease-in-out infinite; }
        .hero-think-2 { animation-delay: 0.25s; }
        .hero-think-3 { animation-delay: 0.5s; }
        .hero-hand   { animation: hero-hand 2.4s ease-in-out infinite; }
        .hero-beat-moment   { animation: hero-beat-moment 5s ease-in-out infinite; }
        .hero-beat-tagline  { animation: hero-beat-tagline 5s ease-in-out infinite; }
        .hero-scanlines {
          background-image: linear-gradient(
            transparent 0px, transparent 19px,
            rgba(0, 108, 255, 0.06) 20px, rgba(0, 108, 255, 0.06) 21px
          );
          background-size: 100% 40px;
          animation: hero-scan 8s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default HeroBeat;
